import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { ARP_PATTERN_ORDER, arpeggiator } from '../audio/Arpeggiator';
import { TRANSPORT_STATES } from '../audio/Scheduler';
import { demoTrack, musicRecorder } from '../audio/transport';
import { endSampling, loadSample, vss30 } from '../audio/synth';
import { midi } from '../midi/MidiBus';
import {
  EDIT_MODES,
  FADER_TO_KEY,
  RECORD_STATES,
  SAMPLE_MODES,
  SAMPLE_SELECTIONS,
  USER_SAMPLE_SLOT,
  type PanelCommand,
  type PanelFader,
  type PanelState,
} from './panelTypes';

const ADSR_DELTA = 0.1;
const LOOP_DELTA = 0.01;
const BPM_DELTA = 10;
const BPM_MIN = 40;
const BPM_MAX = 240;
const BASE_BPM = 60;

export const RECORD_LENGTH = 2000;
export const COUNT_IN = 1500;

/** Cleared automatically after an arp pattern is chosen. */
const CLEAR_SELECTION_MODE_ON_PICK = true;

const MAX_HISTORY = 20;

export const DEFAULT_PANEL_STATE: PanelState = {
  VIBRATO: false,
  EDIT_MODE: EDIT_MODES.DEFAULT,
  SELECTION_MODE: EDIT_MODES.DEFAULT,
  ADSR: [0.0001, 1, 0.5, 1],
  LOOP: false,
  LOOP_END: 1,
  PINGPONG: false,
  REVERSE: false,
  ECHO: false,
  FUZZ: false,
  FILTER: false,
  FILTER_CUTOFF: 1,
  REVERB: false,
  REVERB_SIZE: 0,
  SAMPLE_MODE: SAMPLE_MODES.RECORD,
  SAMPLE_SELECTION: SAMPLE_SELECTIONS[0],
  SAMPLE_SELECTION_VALUE: 0,
  ARP_ENABLED: false,
  ARP_SELECTION: ARP_PATTERN_ORDER[0],
  ARP_SELECTION_VALUE: 0,
  BPM: BASE_BPM * 2,
  BPM_MS: 500,
  RECORD_STATE: RECORD_STATES.STOPPED,
  RECORD_START: 0,
  MUSIC_RECORDER_STATE: TRANSPORT_STATES.STOPPED,
  MUSIC_RECORDER_POS: 0,
  DEMO_STATE: TRANSPORT_STATES.STOPPED,
};

interface PanelStore extends PanelState {
  /** Snapshots pushed on every command, newest last. */
  history: PanelState[];
  /** Command names currently held down, for lit-button feedback. */
  pressed: string[];

  run: (command: PanelCommand) => void;
  setFader: (fader: PanelFader, ratio: number) => void;
  press: (command: string) => void;
  release: (command: string) => void;
  undo: () => void;
  /** Applied by transports; deliberately kept out of the undo history. */
  setTransport: (patch: Partial<PanelState>) => void;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

function bpmToMs(bpm: number): number {
  return 60_000 / bpm;
}

const PANEL_KEYS = Object.keys(DEFAULT_PANEL_STATE) as (keyof PanelState)[];

/** Strips the store's own methods, leaving a plain undo snapshot. */
function snapshot(store: PanelStore): PanelState {
  const state = {} as PanelState;
  for (const key of PANEL_KEYS) Object.assign(state, { [key]: store[key] });
  return state;
}

const withAdsr = (
  state: PanelState,
  index: 0 | 1 | 2 | 3,
  delta: number,
  min = 0,
): Partial<PanelState> => {
  const next = [...state.ADSR] as [number, number, number, number];
  next[index] = clamp(next[index] + delta, min, 1);
  return { ADSR: next };
};

let recordTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Begins a count-in, then a fixed-length sampling take.
 * Returns the immediate state patch; the later transitions arrive via
 * `setTransport` so they do not stack undo entries.
 */
function beginSampling(state: PanelState, overdub: boolean): Partial<PanelState> {
  if (state.RECORD_STATE !== RECORD_STATES.STOPPED) return {};

  if (recordTimer !== null) clearTimeout(recordTimer);
  recordTimer = setTimeout(() => {
    recordTimer = null;
    usePanelStore.getState().setTransport({
      RECORD_STATE: overdub ? RECORD_STATES.OVERWRITING : RECORD_STATES.RECORDING,
      RECORD_START: performance.now(),
    });
    void vss30.record(RECORD_LENGTH, overdub).then(() => {
      // The take is done with the microphone; holding it open would leave the
      // browser's recording indicator lit for the rest of the session.
      endSampling();
      usePanelStore.getState().setTransport({
        RECORD_STATE: RECORD_STATES.STOPPED,
        SAMPLE_SELECTION_VALUE: USER_SAMPLE_SLOT,
        SAMPLE_SELECTION: '',
      });
    });
  }, COUNT_IN);

  return {
    SAMPLE_MODE: overdub ? SAMPLE_MODES.OVERWRITE : SAMPLE_MODES.RECORD,
    RECORD_START: performance.now(),
    RECORD_STATE: RECORD_STATES.COUNTING_IN,
  };
}

/** Pure-ish reducers: each returns the patch its button applies. */
const COMMANDS: Record<PanelCommand, (state: PanelState) => Partial<PanelState>> = {
  Button_AllCancel: () => {
    musicRecorder.stop();
    demoTrack.stop();
    arpeggiator.stopAll();
    midi.panic();
    if (recordTimer !== null) {
      clearTimeout(recordTimer);
      recordTimer = null;
    }
    endSampling();
    return { ...DEFAULT_PANEL_STATE };
  },

  Button_Arp_Stop: () => {
    arpeggiator.stopAll();
    return { ARP_ENABLED: false, ARP_SELECTION_VALUE: 0, ARP_SELECTION: ARP_PATTERN_ORDER[0] };
  },

  Button_Demo_StartStop: () => {
    musicRecorder.stop();
    if (demoTrack.playing) {
      demoTrack.stop();
      midi.panic();
    } else {
      demoTrack.play();
    }
    return { MUSIC_RECORDER_STATE: musicRecorder.state, DEMO_STATE: demoTrack.state };
  },

  Button_MP_PlayStop: () => {
    demoTrack.stop();
    if (musicRecorder.playing) {
      musicRecorder.stop();
      midi.panic();
    } else {
      if (musicRecorder.recording) musicRecorder.stop();
      musicRecorder.play();
    }
    return { DEMO_STATE: demoTrack.state, MUSIC_RECORDER_STATE: musicRecorder.state };
  },

  Button_MP_Record: () => {
    if (musicRecorder.recording) {
      musicRecorder.stop();
    } else {
      musicRecorder.stop();
      musicRecorder.reset();
      musicRecorder.record(true);
    }
    return { MUSIC_RECORDER_STATE: musicRecorder.state };
  },

  Button_Tempo_Down: (state) => {
    const BPM = clamp(state.BPM - BPM_DELTA, BPM_MIN, BPM_MAX);
    return { BPM, BPM_MS: bpmToMs(BPM) };
  },
  Button_Tempo_Up: (state) => {
    const BPM = clamp(state.BPM + BPM_DELTA, BPM_MIN, BPM_MAX);
    return { BPM, BPM_MS: bpmToMs(BPM) };
  },

  // The data-entry wheel moves whichever parameter is in edit mode. "Up" on the
  // panel decreases the value, matching the original hardware's inverted dial.
  Button_DataEntry_Up: (state) => dataEntry(state, -1),
  Button_DataEntry_Down: (state) => dataEntry(state, 1),

  Button_Attack: () => ({ EDIT_MODE: EDIT_MODES.ADSR_A }),
  Button_Decay: () => ({ EDIT_MODE: EDIT_MODES.ADSR_D }),
  Button_Sustain: () => ({ EDIT_MODE: EDIT_MODES.ADSR_S }),
  Button_Release: () => ({ EDIT_MODE: EDIT_MODES.ADSR_R }),

  Button_Loop: (state) => ({
    LOOP: !state.LOOP,
    EDIT_MODE: !state.LOOP ? EDIT_MODES.LOOP_EDIT : EDIT_MODES.DEFAULT,
  }),
  Button_PingPong: (state) => ({ PINGPONG: !state.PINGPONG }),
  Button_Reverse: (state) => {
    vss30.toggleReverse();
    return { REVERSE: !state.REVERSE };
  },
  Button_Echo: (state) => ({ ECHO: !state.ECHO }),
  Button_Fuzz: (state) => ({ FUZZ: !state.FUZZ }),
  Button_Filter: (state) => ({ FILTER: !state.FILTER }),
  Button_Reverb: (state) => ({ REVERB: !state.REVERB }),
  Button_Vibrato: (state) => ({ VIBRATO: !state.VIBRATO }),

  Button_Sample: (state) => beginSampling(state, false),
  Button_Overwrite: (state) => beginSampling(state, true),

  Button_ModeSelect_ArpSelect: (state) => ({
    SELECTION_MODE:
      state.SELECTION_MODE === EDIT_MODES.DEFAULT ? EDIT_MODES.ARP_SELECT : EDIT_MODES.DEFAULT,
  }),

  Button_Select_1: (state) => select(state, 0),
  Button_Select_2: (state) => select(state, 1),
  Button_Select_3: (state) => select(state, 2),
  Button_Select_4: (state) => select(state, 3),
  Button_Select_5: (state) => select(state, 4),
  Button_Select_6: (state) => select(state, 5),
  Button_Select_7: (state) => select(state, 6),
  Button_Select_8: (state) => select(state, 7),
  Button_Select_9: (state) => select(state, 8),
  Button_Select_10: (state) => select(state, 9),
  Button_Select_11: (state) => select(state, 10),
  Button_Select_12: (state) => select(state, 11),
};

function dataEntry(state: PanelState, direction: 1 | -1): Partial<PanelState> {
  switch (state.EDIT_MODE) {
    case EDIT_MODES.LOOP_EDIT:
      return { LOOP_END: clamp(state.LOOP_END + LOOP_DELTA * direction, 0.01, 1) };
    case EDIT_MODES.ADSR_A:
      return withAdsr(state, 0, ADSR_DELTA * direction, 0.0001);
    case EDIT_MODES.ADSR_D:
      return withAdsr(state, 1, ADSR_DELTA * direction);
    case EDIT_MODES.ADSR_S:
      return withAdsr(state, 2, ADSR_DELTA * direction);
    case EDIT_MODES.ADSR_R:
      return withAdsr(state, 3, ADSR_DELTA * direction);
    default:
      return {};
  }
}

function select(state: PanelState, index: number): Partial<PanelState> {
  if (state.SELECTION_MODE === EDIT_MODES.ARP_SELECT) {
    const pattern = ARP_PATTERN_ORDER[index];
    return {
      ARP_SELECTION: pattern,
      ARP_SELECTION_VALUE: index,
      ARP_ENABLED: index !== 0,
      ...(CLEAR_SELECTION_MODE_ON_PICK ? { SELECTION_MODE: EDIT_MODES.DEFAULT } : {}),
    };
  }

  const path = SAMPLE_SELECTIONS[index];
  // The user slot has no file — selecting it just re-arms the live buffer.
  if (path) void loadSample(path).catch(() => {});
  return { SAMPLE_SELECTION: path, SAMPLE_SELECTION_VALUE: index };
}

export const usePanelStore = create<PanelStore>()(
  subscribeWithSelector((set, get) => ({
    ...DEFAULT_PANEL_STATE,
    history: [],
    pressed: [],

    run: (command) => {
      const store = get();
      const patch = COMMANDS[command]?.(snapshot(store));
      if (!patch || Object.keys(patch).length === 0) return;
      const history = [...store.history, snapshot(store)].slice(-MAX_HISTORY);
      set({ ...patch, history });
    },

    setFader: (fader, ratio) => {
      set({ [FADER_TO_KEY[fader]]: clamp(ratio, 0, 1) } as Partial<PanelState>);
    },

    press: (command) => {
      const { pressed } = get();
      if (!pressed.includes(command)) set({ pressed: [...pressed, command] });
    },

    release: (command) => {
      const { pressed } = get();
      if (pressed.includes(command)) set({ pressed: pressed.filter((c) => c !== command) });
    },

    undo: () => {
      const { history } = get();
      const previous = history.at(-1);
      if (!previous) return;
      set({ ...previous, history: history.slice(0, -1) });
    },

    setTransport: (patch) => set(patch),
  })),
);

export const panelActions = {
  run: (command: PanelCommand) => usePanelStore.getState().run(command),
  setFader: (fader: PanelFader, ratio: number) => usePanelStore.getState().setFader(fader, ratio),
  press: (command: string) => usePanelStore.getState().press(command),
  release: (command: string) => usePanelStore.getState().release(command),
  undo: () => usePanelStore.getState().undo(),
};
